"use client";

import { KeyboardEvent, useState } from "react";
import Image, { getImageProps } from "next/image";

import PlusThin from "@/icons/actions/plus-thin.svg";
import Lead from "~/componentsDS/Typography/Lead";
import Overline from "~/componentsDS/Typography/Overline";
import Paragraph from "~/componentsDS/Typography/Paragraph";
import { GTM_EVENT_NAMES } from "~/constants/gtmEvents";
import { useTrackEvent } from "~/hooks/useTrackEvent";
import { cn } from "~/lib/utils";
import { Ingredient } from "./data";

type IngredientCardProps = {
    ingredient: Ingredient;
};

export default function IngredientCard({ ingredient }: IngredientCardProps) {
    const { title, name, nameSuffix, imageUrl, mobileImageUrl, functionText } = ingredient;
    const sendEvent = useTrackEvent();
    const [isOpened, setIsOpened] = useState(false);
    const {
        props: desktopImageProps,
    } = getImageProps({
        src: imageUrl,
        alt: "",
        fill: true,
        sizes: "(min-width: 1024px) 500px, 70vw",
    });

    const handleToggle = () => {
        if (!isOpened) {
            sendEvent({
                event: "user_interaction",
                value: {
                    event_name: GTM_EVENT_NAMES.HP_INTERACTION,
                    widget: "our_science",
                    meta_data: {
                        click_text: name,
                        product_name: "anti-aging face cream"
                    },
                },
            });
        }

        setIsOpened((prev) => !prev);
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            handleToggle();
        }
    };

    return (
        <article
            role="button"
            tabIndex={0}
            aria-expanded={isOpened}
            aria-label={nameSuffix ? `${name} ${nameSuffix}` : name}
            onClick={handleToggle}
            onKeyDown={handleKeyDown}
            className="group relative flex min-h-[188px] cursor-pointer select-none overflow-hidden rounded-2xl p-4 after:pointer-events-none after:absolute after:inset-0 after:z-30 after:rounded-2xl after:border after:border-pureWhite/35 lg:min-h-[248px] lg:p-6"
        >
            <div
                className="pointer-events-none absolute inset-0 rounded-2xl bg-faceCream500/[0.05] backdrop-blur-[2px]"
                aria-hidden="true"
            />

            <div className="pointer-events-none absolute inset-0" aria-hidden="true">
                <picture>
                    <source
                        media="(min-width: 768px)"
                        srcSet={desktopImageProps.srcSet}
                        sizes={desktopImageProps.sizes}
                    />
                    <Image
                        src={mobileImageUrl}
                        alt=""
                        fill
                        sizes="100vw"
                        loading="lazy"
                        className={cn(
                            "object-cover object-center transition-transform duration-500 ease-out",
                            !isOpened && "[@media(hover:hover)_and_(pointer:fine)]:group-hover:scale-110"
                        )}
                    />
                </picture>
            </div>

            <div
                className={cn(
                    "pointer-events-none absolute inset-0 rounded-2xl bg-pureWhite/90 backdrop-blur-[5px] transition-opacity duration-300 md:bg-pureWhite/0",
                    isOpened ? "opacity-100" : "opacity-0"
                )}
                aria-hidden="true"
            />

            <div
                className={cn(
                    "absolute right-4 top-4 z-20 flex size-10 shrink-0 items-center justify-center rounded-full border text-pureWhite backdrop-blur-[10px] transition-all duration-300 lg:right-6 lg:top-6 lg:size-14 lg:border-2",
                    isOpened
                        ? "rotate-45 border-pureWhite/20 bg-trueBlack/30"
                        : "border-pureWhite/50 bg-trueBlack/25 [@media(hover:hover)_and_(pointer:fine)]:group-hover:bg-primary300"
                )}
                aria-hidden="true"
            >
                <PlusThin width={28} height={28} className="size-5 lg:size-7" />
            </div>

            <div className="relative z-10 flex min-h-[148px] w-full flex-col justify-between lg:min-h-[200px]">
                <div className="flex items-start justify-between gap-3">
                    <Lead
                        as="p"
                        font="featureDisplay"
                        size="sm"
                        className={cn(
                            "max-md:text-[24px]",
                            "pr-2 text-primary500 md:text-pureWhite transition-all duration-300",
                            isOpened && "invisible pointer-events-none opacity-0"
                        )}
                    >
                        {title}
                    </Lead>

                    <div className="size-10 shrink-0 lg:size-14" aria-hidden="true" />
                </div>

                <div
                    className={cn(
                        "max-w-[220px] text-primary500 md:text-pureWhite transition-all duration-300",
                        isOpened && "invisible pointer-events-none opacity-0"
                    )}
                >
                    <Paragraph as="h4" size="xl" sizes={{ md: "2xl" }} weight="500">
                        {name}
                    </Paragraph>
                    {nameSuffix && (
                        <Paragraph size="xl" sizes={{ md: "2xl" }} weight="500">
                            {nameSuffix}
                        </Paragraph>
                    )}
                </div>
            </div>

            <div
                className={cn(
                    "absolute inset-0 z-10 flex flex-col p-5 pr-16 bg-pureWhite/80 transition-[opacity,visibility] duration-300 lg:p-6 lg:pr-20",
                    isOpened ? "visible opacity-100" : "invisible pointer-events-none opacity-0"
                )}
            >
                <div className="flex flex-col justify-end h-full">
                    <Overline size="md" className="text-gray400">
                        Function
                    </Overline>
                    <Lead size="xs" sizes={{ lg: "sm" }} font="featureDisplay" className="mt-1 text-primary500">
                        {functionText}
                    </Lead>
                </div>
            </div>
        </article>
    );
}
